import React, { FC, useMemo, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import _isEmpty from 'lodash/isEmpty';
import _sortBy from 'lodash/sortBy';
import _filter from 'lodash/filter';
import ErrorMessage from '@minecraft.error-message';
import { useDrawerSearchFilterNameOrCategory, useDrawerFiltersCounts, useDrawerGlobalLoader } from '@minecraft.drawer';
import { gqlProfileSearchInput } from '@minecraft.graphql-types';
import { useTranslation } from '@blocs.i18n';
import {
  FormItem,
  useFiltersLabel,
  useLazyGenerateSkillLevelsSelectOptions,
  groupDataForHierarchySelect,
  FiltersNameEnum,
  FilterDrawerType,
} from '@blocs.filter';
import { FiltersCategoryNameEnum, FiltersCountNameEnum } from '../../shared/types';
import { SkillLevels, Skills } from '../../FilterDrawerCollection';
import { QUERY_SKILLS } from './SkillsCategoryGraphQL';

interface Props {
  appliedQueryInput?: gqlProfileSearchInput;
  refetchProfileFilterData?: ({ input: object, resultType: string }) => void;
  type: FilterDrawerType;
}

const SkillsCategory: FC<Props> = ({ type, refetchProfileFilterData, appliedQueryInput }) => {
  const { t } = useTranslation();
  const filtersLabelOfAudience = useFiltersLabel(type);
  const generateSkillLevelsSelectOptions = useLazyGenerateSkillLevelsSelectOptions(true);

  const { data, error, loading } = useQuery(QUERY_SKILLS);

  const { isCategoryMatchedSearch, shouldCategoryFilterBeVisible, assignOptionsToCategoryFilters } =
    useDrawerSearchFilterNameOrCategory(FiltersCategoryNameEnum.skills);
  const { filtersCounts } = useDrawerFiltersCounts();

  const { skillOptions, skillLevelsOptions } = useMemo(() => {
    const { skills = [], skillLevels = [] } = data || {};

    return {
      skillOptions: _filter(
        groupDataForHierarchySelect(skills, filtersCounts?.[FiltersCountNameEnum.skillsId]),
        (skill) => !_isEmpty(skill.options)
      ),
      skillLevelsOptions: generateSkillLevelsSelectOptions(
        _sortBy(
          _filter(skillLevels, (skillLevel) => skillLevel.enabled),
          'sortOrder'
        ),
        filtersCounts?.[FiltersCountNameEnum.skillsSkillLevelId]
      ),
    };
  }, [data, filtersCounts]);

  useDrawerGlobalLoader(loading);

  useEffect(() => {
    if (skillOptions.length > 0) {
      assignOptionsToCategoryFilters({
        [FiltersNameEnum.skills]: skillOptions,
        [FiltersNameEnum.skillLevels]: skillLevelsOptions,
      });
    }
  }, [skillOptions]);

  if (error) {
    return <ErrorMessage error={error} />;
  }

  return (
    isCategoryMatchedSearch && (
      <>
        {shouldCategoryFilterBeVisible(FiltersNameEnum.skills) && (
          <FormItem $fullWidth $marginValue={2}>
            <Skills
              appliedQueryInput={appliedQueryInput}
              name={FiltersNameEnum.skills}
              label={filtersLabelOfAudience[FiltersNameEnum.skills]}
              refetchProfileFilterData={refetchProfileFilterData}
            />
          </FormItem>
        )}
        {shouldCategoryFilterBeVisible(FiltersNameEnum.skillLevels) && (
          <FormItem $fullWidth $marginValue={2}>
            <SkillLevels
              name={FiltersNameEnum.skillLevels}
              label={filtersLabelOfAudience[FiltersNameEnum.skillLevels]}
              subLabel={t('common:filtersName.skillLevels.message')}
            />
          </FormItem>
        )}
      </>
    )
  );
};

export default SkillsCategory;
